import React, { useEffect, useRef, useState } from "react";
import { createBugReport } from "../../api/profileService";
import { useAuth } from "../../context/AuthContext";

export default function Contact() {
  const { user } = useAuth();
  const [form, setForm] = useState({ subject: "", email: "", message: "" });

  useEffect(() => {
    if (user?.email) {
      setForm((current) => ({ ...current, email: current.email || user.email }));
    }
  }, [user?.email]);
  const [attachment, setAttachment] = useState(null);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const submittingRef = useRef(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setSent(false);
    setError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
      if (!allowed.includes(file.type)) {
        setError("Fichier non autorisé. Seules les images (JPG, PNG, GIF, WebP) et les PDF sont acceptés.");
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Fichier trop volumineux (max 5 Mo).");
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }
    setAttachment(file);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setError("");
    setSent(false);

    try {
      const formData = new FormData();
      formData.append("subject", form.subject);
      formData.append("email", form.email);
      formData.append("message", form.message);
      if (attachment) formData.append("attachment", attachment);

      await createBugReport(formData);
      setSent(true);
      setForm({ subject: "", email: "", message: "" });
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Envoi impossible. Réessaie plus tard."
      );
    } finally {
      setIsSubmitting(false);
      submittingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-600">
            Support
          </p>
          <h1 className="mt-3 text-4xl font-black text-slate-950">Contactez-nous</h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600">
            Une question, un retour ou un problème à signaler ? Envoie un message
            clair avec ton pseudo.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-5xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[1fr_0.75fr] lg:px-8">
        <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4">
            <label className="space-y-2">
              <span className="text-sm font-bold text-slate-700">Sujet</span>
              <input
                required
                type="text"
                value={form.subject}
                onChange={(event) => updateField("subject", event.target.value)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-800"
                placeholder="Ex : Problème de compte"
              />
            </label>

            <label className="space-y-2">
              <span className="text-sm font-bold text-slate-700">Email</span>
              <input
                required
                type="email"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-800"
                placeholder="ton.email@example.com"
              />
            </label>

            <label className="space-y-2">
              <span className="text-sm font-bold text-slate-700">Message</span>
              <textarea
                required
                rows={7}
                value={form.message}
                onChange={(event) => updateField("message", event.target.value)}
                className="w-full resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-800"
                placeholder="Décris ta demande..."
              />
            </label>

            <div className="space-y-2">
              <span className="text-sm font-bold text-slate-700">
                Pièce jointe{" "}
                <span className="font-normal text-slate-400">(optionnel — image ou PDF, max 5 Mo)</span>
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-xs file:font-bold file:text-slate-700 hover:file:bg-slate-200"
              />
              {attachment && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>{attachment.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachment(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="text-rose-500 hover:underline"
                  >
                    Supprimer
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
            </button>
          </div>

          {sent && (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              Message envoyé avec succès. Nous reviendrons vers toi prochainement.
            </div>
          )}
        </form>

        <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-black text-slate-950">Avant d'ecrire</h2>
          <div className="mt-4 space-y-4 text-sm leading-7 text-slate-600">
            <p>Pour un bug en jeu, indique la catégorie, l'heure et ton pseudo.</p>
            <p>Pour un problème de compte, précise le pseudo et l'adresse email utilisée.</p>
            <p>Pour une modération urgente, utilise aussi les boutons de signalement disponibles dans l'application.</p>
          </div>
        </aside>
      </section>
    </div>
  );
}
